import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, HelpCircle, RotateCcw, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useOnboarding } from '../onboarding/OnboardingContext';

const railIconClass = 'h-[18px] w-[18px] shrink-0';

/**
 * Subtle guide launcher — feature guide, restart walkthrough, FAQ, settings.
 */
export function GuideLauncher({ variant = 'icon', collapsed = false, className = '' }) {
  const { requestWalkthroughReplay } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();

  const restartWalkthrough = () => {
    requestWalkthroughReplay();
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const trigger = (
    <DropdownMenuTrigger asChild>
      {variant === 'text' ? (
        <Button variant="ghost" size="sm" className={cn('gap-1.5 text-[#5c534a]', className)} data-testid="guide-launcher">
          <BookOpen className="h-4 w-4 opacity-80" />
          <span className="text-sm">Guide</span>
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            collapsed
              ? 'h-11 w-11 rounded-xl text-[hsl(32_10%_42%)] hover:bg-[hsl(42_26%_97%)] hover:text-[hsl(24_12%_22%)]'
              : 'text-[#5c534a] hover:bg-[#ebe6dc]/60',
            className,
          )}
          data-testid="guide-launcher"
          aria-label="Open guide"
        >
          <HelpCircle className={cn(collapsed ? railIconClass : 'h-5 w-5')} strokeWidth={1.75} />
        </Button>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right" className="text-xs">
            Open guide
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent align={collapsed ? 'start' : 'end'} side={collapsed ? 'right' : 'bottom'} className="w-52">
        <DropdownMenuItem asChild>
          <Link to="/guide" className="flex cursor-pointer items-center gap-2">
            <BookOpen className="h-4 w-4 opacity-70" />
            Feature guide
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/help" className="flex cursor-pointer items-center gap-2" data-testid="guide-launcher-help">
            <HelpCircle className="h-4 w-4 opacity-70" />
            Help &amp; Guides
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem className="flex cursor-pointer items-center gap-2" onSelect={restartWalkthrough}>
          <RotateCcw className="h-4 w-4 opacity-70" />
          Restart walkthrough
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/faq" className="flex cursor-pointer items-center gap-2">
            <HelpCircle className="h-4 w-4 opacity-70" />
            FAQ &amp; support
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex cursor-pointer items-center gap-2">
            <Settings className="h-4 w-4 opacity-70" />
            Account settings
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default GuideLauncher;
